import { Grid, makeStyles, Tooltip, Typography } from '@material-ui/core';
import i18n from 'i18n';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import React, { useState } from 'react';
import FlipIcon from '@material-ui/icons/Flip';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Publication, Step } from 'modules/environments/interfaces';
import { formatDateWithMoment, formatDuration } from 'utils/helpers';
import clsx from 'clsx';
import styles from '../styles';

const useStyles = makeStyles(styles);

interface HeaderDetailsProps {
  itemFields: Publication;
}

const statusMap = {
  SUCCESS: {
    icon: CheckCircleIcon,
    textKey: 'SUCCESSFUL_PUBLICATION_KEY',
    className: 'statusValid',
    iconClassName: 'statusIconValid',
    textClassName: 'valid',
  },
  FAILED: {
    icon: HighlightOffIcon,
    textKey: 'FAILED_PUBLICATION_KEY',
    className: 'statusInvalid',
    iconClassName: 'statusIconInvalid',
    textClassName: 'invalid',
  },
  TIMEOUT: {
    icon: HighlightOffIcon,
    textKey: 'FAILED_PUBLICATION_KEY',
    className: 'statusInvalid',
    iconClassName: 'statusIconInvalid',
    textClassName: 'invalid',
  },
  WARNING: {
    icon: WarningIcon,
    textKey: 'WARNING_PUBLICATION_KEY',
    className: 'statusWarning',
    iconClassName: 'statusIconWarning',
    textClassName: 'warning',
  },
  RUNNING: {
    icon: FlipIcon,
    textKey: 'IN_PROGRESS_KEY',
    className: 'statusRunning',
    iconClassName: 'statusIconRunning',
    textClassName: 'running',
  },
};

const HeaderDetails = ({ itemFields }: HeaderDetailsProps) => {
  const classes = useStyles();
  const [expandedSteps, setExpandedSteps] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleExpandStep = (id: string, parentId: string | null = null) => {
    const uniqueId = parentId ? `${parentId}-${id}` : id;
    setExpandedSteps(prevState => ({
      ...prevState,
      [uniqueId]: !prevState[uniqueId],
    }));
  };

  const renderStatus = () => {
    const status = statusMap[itemFields.status];
    if (!status) return null;

    const StatusIcon = status.icon;
    return (
      <Grid item className={classes[status.className]}>
        <StatusIcon className={classes[status.iconClassName]} />
        <Typography className={classes[status.textClassName]}>
          {i18n.t(status.textKey)}
        </Typography>
      </Grid>
    );
  };

  const statusValue = (status: string) => {
    const advisorTypeMap = {
      SUCCESS: i18n.t('PUBLISH_SUCCESS_KEY'),
      FAILED: i18n.t('PUBLISH_FAILED_KEY'),
      TIMEOUT: i18n.t('PUBLISH_TIMEOUT_KEY'),
      WARNING: i18n.t('PUBLISH_WARNING_KEY'),
      RUNNING: i18n.t('PUBLISH_RUNNING_KEY'),
      SKIPPED: i18n.t('PUBLISH_SKIPPED_KEY'),
    };
    return advisorTypeMap[status];
  };

  const InfoItem = ({ label, value }) =>
    value && (
      <Grid item className={classes.itemInfoDetail}>
        <Typography component="div" className={classes.infoTitle}>
          {`${i18n.t(label)}:`}
        </Typography>
        <Tooltip title={value} arrow>
          <Typography component="div" className={classes.infoValue}>
            {value}
          </Typography>
        </Tooltip>
      </Grid>
    );

  const renderStep = (
    step: Step,
    level = 0,
    parentId: string | null = null
  ) => {
    const stepClasses = clsx(classes.modalBodyTitle, {
      [classes.modalBodyTitleSuccess]: step?.status === 'SUCCESS',
      [classes.modalBodyTitleError]:
        step?.status === 'FAILED' || step?.status === 'TIMEOUT',
      [classes.modalBodyTitleSkipped]: step?.status === 'SKIPPED',
      [classes.modalBodyTitleRunning]: step?.status === 'RUNNING',
      [classes.modalBodyTitleWarning]: step?.status === 'WARNING',
    });

    const uniqueId = parentId ? `${parentId}-${step?.id}` : step?.id;
    const isExpanded = expandedSteps[uniqueId] || false;

    return (
      <Grid
        container
        key={step?.id}
        className={stepClasses}
        style={{
          marginLeft: `${level * 16}px`,
          marginRight: `${level * 16}px`,
          width: level > 0 ? '95%' : '100%',
        }}
      >
        <Grid
          container
          justify="space-between"
          alignItems="center"
          onClick={() => toggleExpandStep(step?.id, parentId)}
        >
          <Grid item className={classes.detailExpandGrid}>
            {isExpanded ? (
              <ExpandMoreIcon className={classes.expandIcon} />
            ) : (
              <ExpandLessIcon className={classes.expandIcon} />
            )}
            <Typography className={classes.modalBodyTitleValue}>
              {step?.name}
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              className={clsx(classes.modalBodyTitleStatus, {
                [classes.modalBodyTitleStatusSuccess]:
                  step?.status === 'SUCCESS',
                [classes.modalBodyTitleStatusError]:
                  step?.status === 'FAILED' || step?.status === 'TIMEOUT',
                [classes.modalBodyTitleStatusSkipped]:
                  step?.status === 'SKIPPED',
                [classes.modalBodyTitleStatusRunning]:
                  step?.status === 'RUNNING',
                [classes.modalBodyTitleStatusWarning]:
                  step?.status === 'WARNING',
              })}
            >
              {statusValue(step?.status)}
            </Typography>
          </Grid>
        </Grid>
        {isExpanded && (
          <Grid container className={classes.modalHeaderInfoSection}>
            <Grid item>
              <InfoItem label="DETAILS_KEY" value={step?.details} />
              <InfoItem label="DESCRIPTION_KEY" value={step?.description} />
              <InfoItem
                label="START_END_KEY"
                value={`${formatDateWithMoment(
                  step?.started_at
                )} / ${formatDateWithMoment(step?.finished_at)}`}
              />
              <InfoItem
                label="DURATION_KEY"
                value={
                  step?.duration_milliseconds !== null &&
                  formatDuration(step?.duration_milliseconds)
                }
              />
            </Grid>
          </Grid>
        )}
        {isExpanded &&
          step?.sub_steps?.length > 0 &&
          step?.sub_steps.map((subStep: Step) =>
            renderStep(subStep, level + 1, uniqueId)
          )}
      </Grid>
    );
  };

  return (
    <Grid container>
      <Grid container justify="center" className={classes.renderStatus}>
        {renderStatus()}
      </Grid>
      <Grid container className={classes.modalHeaderInfo}>
        <InfoItem
          label="MADE_BY_KEY"
          value={`${itemFields?.operator?.first_name} ${itemFields?.operator?.last_name} (${itemFields?.operator?.email})`}
        />
        <InfoItem label="MODEL_VERSION_KEY" value={itemFields?.model_version} />
        <InfoItem
          label="START_END_KEY"
          value={`${formatDateWithMoment(
            itemFields?.started_at
          )} / ${formatDateWithMoment(itemFields?.finished_at)}`}
        />
        <InfoItem
          label="DURATION_KEY"
          value={
            itemFields.status !== 'RUNNING' &&
            itemFields?.duration_milliseconds !== null
              ? formatDuration(itemFields?.duration_milliseconds)
              : i18n.t('IN_PROGRESS_KEY')
          }
        />
      </Grid>
      {itemFields.steps.map((step: Step) => renderStep(step))}
    </Grid>
  );
};

export default HeaderDetails;
